import React from "react";
import CodeBlock from "@/components/CodeBlock";

const ServiceDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        CLI Command — add service
      </h2>

      <p className="mb-6">
        The <strong>add service</strong> command generates both the Service
        Interface and Service Implementation inside the Application layer. This
        ensures a clean Domain-Driven Design structure and enforces separation
        of concerns across AxisNet-based projects.
      </p>

      {/* BASIC USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Basic Usage
        </h3>

        <CodeBlock
          language="bash"
          code={`axisnet add service --name Example`}
        />

        <p className="mt-4">
          This command generates two files inside your Application layer:
        </p>

        <CodeBlock
          language="bash"
          code={`Application/
  └── DataAccess/
      └── Services/
          ├── Interfaces/
          │     └── IExampleService.cs
          └── Implementations/
                └── ExampleService.cs`}
        />
      </div>

      {/* TEMPLATE OUTPUT */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Generated Interface (Template)
        </h3>

        <CodeBlock
          language="csharp"
          code={`using {{ProjectName}}.Application.DataAccess.Services.Interfaces;

namespace {{ProjectName}}.Application.DataAccess.Services.Interfaces;

public interface I{{ServiceName}}Service
{
    
}`}
        />

        <h3 className="text-xl font-semibold text-emerald-400 mb-3 mt-6">
          Generated Implementation (Template)
        </h3>

        <CodeBlock
          language="csharp"
          code={`using {{ProjectName}}.Application.DataAccess.Services.Interfaces;

namespace {{ProjectName}}.Application.DataAccess.Services.Implementations;

public class {{ServiceName}}Service : I{{ServiceName}}Service
{
    
}`}
        />
      </div>

      {/* AUTO DI REGISTRATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Automatic Dependency Injection Registration
        </h3>

        <p className="mb-4">
          The CLI automatically injects the generated service into the
          Application layer’s <code>DependencyInjection.cs</code> file. This
          ensures that new service types are instantly available to the DI
          container.
        </p>

        <p className="mb-4">Inserted line example:</p>

        <CodeBlock
          language="csharp"
          code={`services.AddScoped<I{{ServiceName}}Service, {{ServiceName}}Service>();`}
        />

        <p className="mt-4">
          The insertion is placed <strong>above</strong> the first occurrence of{" "}
          <code>return services;</code> ensuring clean structure and predictable
          ordering.
        </p>
      </div>

      {/* PROJECT STRUCTURE VALIDATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Project Structure Validation
        </h3>

        <p className="mb-4">
          If <code>validate-project-structure</code> flag is enabled, the CLI
          will ensure that the required directories exist before generating
          service files.
        </p>

        <CodeBlock
          language="bash"
          code={`axisnet flags set validate-project-structure on`}
        />
      </div>

      {/* GIT AUTO SYNC */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Git Auto-Sync Support
        </h3>

        <p className="mb-4">
          If <code>git-sync</code>, <code>git-commit</code>, or
          <code>git-push</code> flags are enabled, AxisNet CLI will
          automatically:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>stage generated service files</li>
          <li>commit with autogenerated message</li>
          <li>push to remote repository</li>
        </ul>

        <CodeBlock
          language="bash"
          code={`axisnet flags set git-sync on
axisnet flags set git-commit on
axisnet flags set git-push on`}
        />
      </div>
    </section>
  );
};

export default ServiceDoc;
